import React from 'react';
import styled from '@emotion/styled';

/**
 * ScaleItem — one statement on agreement 1–5: five points spread across the
 * row, joined by a line, each with its own label under it, and "en osaa
 * sanoa" on a row of its own below, never as a sixth point on the scale.
 * The self-assessment's items are this piece (#693). Pure: value in,
 * onChange out. value: 1..5 agreed, null = does not know, undefined = not
 * answered yet.
 */
const Row = styled.div`
  display: flex; flex-direction: column; gap: 0.9rem; padding: 1rem 1rem 0.9rem; border-radius: 10px;
  background: #141821; border: 1px solid #232a36; color: #e8e8ef; line-height: 1.45;
`;
const Track = styled.div`
  position: relative; display: flex; align-items: flex-start;
  /* the line joins the centres of the first and last of five equal cells */
  &::before { content: ''; position: absolute; left: 10%; right: 10%; top: 1.2rem; height: 2px; background: #2f3745; }
`;
const Cell = styled.div`flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 0.45rem; position: relative;`;
const Pt = styled.button`
  all: unset; cursor: pointer; box-sizing: border-box; width: 2.4rem; height: 2.4rem; border-radius: 999px;
  display: inline-grid; place-items: center; font-weight: 700; font-variant-numeric: tabular-nums;
  border: 2px solid ${(p) => (p.$on ? '#8ec1f0' : '#3a4457')}; color: ${(p) => (p.$on ? '#0b1020' : '#c9cbd6')};
  background: ${(p) => (p.$on ? '#8ec1f0' : '#141821')};
  transition: border-color 0.15s, background 0.15s;
  &:hover, &:focus-visible { border-color: #8ec1f0; }
  &:disabled { cursor: default; opacity: 0.7; }
`;
const Label = styled.span`
  font-size: 0.72rem; line-height: 1.25; text-align: center; color: ${(p) => (p.$on ? '#e8e8ef' : '#7b8794')};
  hyphens: auto; overflow-wrap: anywhere; padding: 0 0.15rem;
`;
const Below = styled.div`display: flex; justify-content: center;`;
const Unknown = styled.button`
  all: unset; cursor: pointer; padding: 0.45rem 0.9rem; border-radius: 999px; font-size: 0.82rem; font-weight: 600;
  border: 1px solid ${(p) => (p.$on ? '#c9cbd6' : '#2f3745')}; color: ${(p) => (p.$on ? '#e8e8ef' : '#7b8794')};
  background: ${(p) => (p.$on ? 'rgba(201, 203, 214, 0.12)' : 'transparent')};
  &:hover, &:focus-visible { border-color: #c9cbd6; color: #e8e8ef; }
  &:disabled { cursor: default; opacity: 0.7; }
`;

export const SCALE = [1, 2, 3, 4, 5];

/**
 * @param {{ text: string, value?: number|null, onChange, labels: {1,2,3,4,5, unknown}, disabled }} props
 */
export default function ScaleItem({ text, value, onChange, labels, disabled = false }) {
  const pick = (v) => !disabled && onChange?.(value === v ? undefined : v);
  return (
    <Row role="radiogroup" aria-label={text}>
      <div>{text}</div>
      <Track>
        {SCALE.map((n) => (
          <Cell key={n}>
            <Pt type="button" role="radio" aria-checked={value === n} aria-label={`${n} · ${labels?.[n] || ''}`} $on={value === n} disabled={disabled} onClick={() => pick(n)}>{n}</Pt>
            <Label $on={value === n} lang="fi">{labels?.[n]}</Label>
          </Cell>
        ))}
      </Track>
      <Below>
        <Unknown type="button" role="radio" aria-checked={value === null} $on={value === null} disabled={disabled} onClick={() => pick(null)}>{labels?.unknown}</Unknown>
      </Below>
    </Row>
  );
}
